import React from "react";
import { US_STATES } from "@/lib/geo";
import { NameHistory, validateNames } from "@/components/wizard/NameHistory";

export type Personal = Record<string, any>;

const F = ({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) => (
  <label className="block">
    <span className="field-label">{label}</span>
    {children}
    {hint && <span className="field-hint block">{hint}</span>}
  </label>
);

export function validatePersonal(p: Personal, short = false): string | null {
  const req = short ? [["first_name", "First name"], ["last_name", "Last name"], ["dob", "Date of birth"], ["email", "Email"], ["dl_number", "Driver's license (write N/A if none)"]]
    : [["first_name", "First name"], ["last_name", "Last name"], ["dob", "Date of birth"], ["email", "Email"], ["phone", "Phone"]];
  for (const [k, l] of req) {
    if (!p[k]?.trim()) return `${l} is required.`;
  }
  if (!p.gender) return "Please choose your gender.";
  const nm = validateNames(p);
  if (nm) return nm;
  if (!/^\S+@\S+\.\S+$/.test(p.email)) return "Please enter a valid email address.";
  if (short) return null;
  if (!p.employment_status) return "Please choose your current employment status.";
  if (["Employed", "Self-employed"].includes(p.employment_status) && !p.employer?.trim()) return "Please enter your employer's name, location and phone.";
  const ssn = (p.ssn || "").replace(/\D/g, "");
  if (!(p.ssn || "").startsWith("***") && ssn.length !== 9) return "Please enter your full 9-digit Social Security number.";
  if (!/^\S+@\S+\.\S+$/.test(p.email)) return "Please enter a valid email address.";
  return null;
}

export default function StepPersonal({ p, onChange, short = false }: { p: Personal; onChange: (p: Personal) => void; short?: boolean }) {
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => onChange({ ...p, [k]: e.target.value });
  return (
    <div className="space-y-6">
      <div className="grid gap-6 sm:grid-cols-3">
        <F label="First name"><input data-testid="applicant-first-name-input" className="field-input" value={p.first_name || ""} onChange={set("first_name")} /></F>
        <F label="Middle name"><input data-testid="applicant-middle-name-input" className="field-input" value={p.middle_name || ""} onChange={set("middle_name")} /></F>
        <F label="Last name"><input data-testid="applicant-last-name-input" className="field-input" value={p.last_name || ""} onChange={set("last_name")} /></F>
      </div>
      <F label="Gender">
        <select data-testid="applicant-gender-select" className="field-input" value={p.gender || ""} onChange={set("gender")}>
          <option value="">Select…</option><option value="Female">Female</option><option value="Male">Male</option><option value="Prefer not to say">Prefer not to say</option>
        </select>
      </F>
      <NameHistory p={p} onChange={onChange} />
      <div className="grid gap-6 sm:grid-cols-2">
        <F label="Date of birth"><input data-testid="applicant-dob-input" type="date" className="field-input" value={p.dob || ""} onChange={set("dob")} /></F>
        {!short && <F label="Social Security number" hint="Encrypted and only seen by authorized staff.">
          <input
            data-testid="applicant-ssn-input"
            inputMode="numeric"
            autoComplete="off"
            className="field-input font-mono tracking-widest"
            placeholder="123-45-6789"
            value={p.ssn || ""}
            onFocus={() => (p.ssn || "").startsWith("***") && onChange({ ...p, ssn: "" })}
            onChange={set("ssn")}
          />
        </F>}
      </div>
      <div className="grid gap-6 sm:grid-cols-2">
        <F label={short ? "Driver's license number" : "Driver's license number (optional)"} hint={short ? "Provide driver's license number and state or enter N/A" : undefined}><input data-testid="applicant-dl-input" autoComplete="off" className="field-input" value={p.dl_number || ""} onChange={set("dl_number")} /></F>
        <F label="License state">
          <select data-testid="applicant-dl-state-select" className="field-input" value={p.dl_state || ""} onChange={set("dl_state")}>
            <option value="">Select…</option>
            {US_STATES.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
          </select>
        </F>
      </div>
      <div className="grid gap-6 sm:grid-cols-2">
        <F label="Email address"><input data-testid="applicant-email-input" type="email" className="field-input" value={p.email || ""} onChange={set("email")} /></F>
        <F label={short ? "Phone number (optional)" : "Phone number"}><input data-testid="applicant-phone-input" type="tel" className="field-input" value={p.phone || ""} onChange={set("phone")} /></F>
      </div>
      {!short && <div className="grid gap-6 sm:grid-cols-2">
        <F label="Current employment">
          <select data-testid="applicant-employment-select" className="field-input" value={p.employment_status || ""} onChange={set("employment_status")}>
            <option value="">Select…</option>
            {["Employed", "Self-employed", "Unemployed", "Retired", "Student", "Homemaker"].map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </F>
        <F label="Employer" hint="Name, location (city, state) and phone number.">
          <input data-testid="applicant-employer-input" className="field-input" value={p.employer || ""} onChange={set("employer")} />
        </F>
      </div>}
    </div>
  );
}
